import React, { useMemo, useState, useImperativeHandle, forwardRef, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { playClack } from "@/lib/sound";

// Soroban: 1 upper bead (=5) + 4 lower beads (=1) per rod.
// `onesRodIndex` = which rod (left→right index) represents the ones place.
// Default = rods - 1 (rightmost) for the classic 4-rod layout.
// For a 13-rod abacus we pass 6 so the ones digit sits on the CENTER rod.
const WHOLE_LABELS = ["1s", "10s", "100s", "1K", "10K", "100K", "1M", "10M", "100M", "1B", "10B", "100B", "1T"];

function makeInitialState(rods) {
  return Array.from({ length: rods }, () => ({ upperActive: false, lowerActiveCount: 0 }));
}

function computeValue(state, onesRodIndex) {
  let value = 0;
  for (let i = 0; i < state.length; i++) {
    const digit = (state[i].upperActive ? 5 : 0) + state[i].lowerActiveCount;
    const power = onesRodIndex - i;
    value += digit * Math.pow(10, power);
  }
  // Kill floating-point noise
  return Math.round(value * 1e6) / 1e6;
}

function numberToState(num, rods, onesRodIndex) {
  const state = makeInitialState(rods);
  if (!num || num < 0) return state;
  const whole = String(Math.floor(num));
  for (let i = 0; i < whole.length; i++) {
    const digit = Number(whole[whole.length - 1 - i]);
    const rod = onesRodIndex - i;
    if (rod < 0) break;
    state[rod].upperActive = digit >= 5;
    state[rod].lowerActiveCount = digit >= 5 ? digit - 5 : digit;
  }
  return state;
}

function labelFor(rodIdx, onesRodIndex) {
  const power = onesRodIndex - rodIdx;
  if (power >= 0) return WHOLE_LABELS[power] || `10^${power}`;
  if (power === -1) return "0.1";
  if (power === -2) return "0.01";
  if (power === -3) return "0.001";
  return `10⁻${-power}`;
}

function formatValue(v) {
  if (Number.isInteger(v)) return String(v);
  // Trim trailing zeros
  return String(parseFloat(v.toFixed(6)));
}

const VirtualAbacus = forwardRef(function VirtualAbacus(
  { rods = 4, onValueChange, readOnly = false, initialValue = 0, onesRodIndex },
  ref
) {
  const resolvedOnes = onesRodIndex == null ? rods - 1 : onesRodIndex;

  const [state, setState] = useState(() =>
    initialValue ? numberToState(initialValue, rods, resolvedOnes) : makeInitialState(rods)
  );

  useEffect(() => {
    if (readOnly) setState(numberToState(initialValue, rods, resolvedOnes));
  }, [readOnly, initialValue, rods, resolvedOnes]);

  const value = useMemo(() => computeValue(state, resolvedOnes), [state, resolvedOnes]);

  useEffect(() => {
    if (onValueChange) onValueChange(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const reset = useCallback(() => {
    if (readOnly) return;
    setState(makeInitialState(rods));
    playClack();
  }, [rods, readOnly]);

  useImperativeHandle(ref, () => ({ reset, getValue: () => value }), [reset, value]);

  const toggleUpper = (rodIdx) => {
    if (readOnly) return;
    playClack();
    setState((prev) => prev.map((r, i) => (i === rodIdx ? { ...r, upperActive: !r.upperActive } : r)));
  };
  const setLower = (rodIdx, beadIdx) => {
    if (readOnly) return;
    playClack();
    setState((prev) => prev.map((r, i) => {
      if (i !== rodIdx) return r;
      const isActive = beadIdx < r.lowerActiveCount;
      const newCount = isActive ? beadIdx : beadIdx + 1;
      return { ...r, lowerActiveCount: newCount };
    }));
  };

  // Sizing
  const rodWidth = rods > 8 ? 46 : 56;
  const beadHeight = rods > 8 ? 22 : 26;
  const beadWidth = rods > 8 ? 38 : 44;
  const upperZoneHeight = rods > 8 ? 54 : 60;
  const lowerZoneHeight = 4 * beadHeight + 24;
  const totalHeight = upperZoneHeight + 24 + lowerZoneHeight + 40;

  // Style tokens — active beads must READ IMMEDIATELY as "counted"
  const upperActiveBg = "linear-gradient(180deg, #FF7A7F 0%, #FF5A5F 50%, #E23B40 100%)";
  const upperInactiveBg = "linear-gradient(180deg, #E8B8BA 0%, #D48D91 50%, #B76A6E 100%)";
  const lowerActiveBg = "linear-gradient(180deg, #FFDD8A 0%, #FFD166 50%, #E5B247 100%)";
  const lowerInactiveBg = "linear-gradient(180deg, #E8D9B4 0%, #D2BE8A 50%, #A69566 100%)";
  const activeRingShadow = "0 0 0 2px #ffffff, 0 0 0 4px rgba(255,255,255,0.35), inset -2px -4px 6px rgba(0,0,0,0.18), 0 4px 6px rgba(0,0,0,0.20)";
  const inactiveShadow = "inset -2px -4px 6px rgba(0,0,0,0.18), 0 2px 3px rgba(0,0,0,0.12)";

  return (
    <div data-testid="virtual-abacus" data-abacus-rods={rods} data-abacus-readonly={readOnly ? "true" : "false"} data-ones-rod-index={resolvedOnes} className="w-full">
      <div className="rounded-3xl p-3 sm:p-5 bg-sky shadow-[0_10px_30px_rgba(0,166,251,0.25),inset_0_-4px_0_0_rgba(0,0,0,0.15)]">
        <div className="rounded-2xl bg-[#0091DE] p-3 sm:p-5 shadow-[inset_0_4px_10px_rgba(0,0,0,0.25)] overflow-x-auto">
          <div
            className="relative mx-auto"
            style={{ width: rods * rodWidth, height: totalHeight, minWidth: rods * rodWidth }}
          >
            {/* Ones-column highlight (subtle vertical band behind the ones rod) */}
            <div
              aria-hidden
              className="absolute top-0 bottom-8 rounded-lg"
              style={{
                left: resolvedOnes * rodWidth + 2,
                width: rodWidth - 4,
                background: "rgba(255,255,255,0.10)",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.15)",
              }}
            />

            {/* Rods */}
            {Array.from({ length: rods }).map((_, rodIdx) => (
              <div
                key={`rod-${rodIdx}`}
                className="absolute top-0 bottom-8 w-[3px] rounded-full bg-slate-300/70"
                style={{ left: rodIdx * rodWidth + rodWidth / 2 - 1.5 }}
              />
            ))}

            {/* Reckoning bar */}
            <div
              className="absolute left-0 right-0 h-3.5 bg-canvas rounded-full shadow-[0_2px_0_0_rgba(0,0,0,0.15)]"
              style={{ top: upperZoneHeight - 2 }}
            >
              {/* Marker dot on the ones rod */}
              <div
                className="absolute w-2.5 h-2.5 rounded-full bg-watermelon border-2 border-white"
                style={{ left: resolvedOnes * rodWidth + rodWidth / 2 - 5, top: -1 }}
                aria-hidden
              />
            </div>

            {/* Upper beads */}
            {state.map((r, rodIdx) => {
              const x = rodIdx * rodWidth + rodWidth / 2;
              const y = r.upperActive ? upperZoneHeight - beadHeight - 2 : 4;
              return (
                <motion.button
                  key={`upper-${rodIdx}`}
                  data-testid={`abacus-upper-bead-rod-${rodIdx}`}
                  data-active={r.upperActive ? "true" : "false"}
                  aria-label={`Rod ${rodIdx + 1} upper bead (value 5)`}
                  onClick={() => toggleUpper(rodIdx)}
                  disabled={readOnly}
                  className={`absolute rounded-full flex items-center justify-center focus:outline-none ${readOnly ? "cursor-default" : ""} ${r.upperActive ? "" : "opacity-55"}`}
                  style={{
                    left: x - beadWidth / 2,
                    width: beadWidth,
                    height: beadHeight,
                    background: r.upperActive ? upperActiveBg : upperInactiveBg,
                    border: r.upperActive ? "1px solid rgba(0,0,0,0.25)" : "1px solid rgba(0,0,0,0.10)",
                    boxShadow: r.upperActive ? activeRingShadow : inactiveShadow,
                    filter: r.upperActive ? "none" : "saturate(0.55)",
                  }}
                  animate={{ top: y }}
                  transition={readOnly ? { duration: 0 } : { type: "spring", stiffness: 500, damping: 24 }}
                  whileHover={readOnly ? undefined : { scale: 1.05 }}
                  whileTap={readOnly ? undefined : { scale: 0.95 }}
                />
              );
            })}

            {/* Lower beads */}
            {state.map((r, rodIdx) => {
              const x = rodIdx * rodWidth + rodWidth / 2;
              const barBottom = upperZoneHeight + 12;
              return Array.from({ length: 4 }).map((_, beadIdx) => {
                const isActive = beadIdx < r.lowerActiveCount;
                const topActive = barBottom + 8 + beadIdx * (beadHeight + 2);
                const topInactive = barBottom + lowerZoneHeight - (4 - beadIdx) * (beadHeight + 2) - 4;
                const y = isActive ? topActive : topInactive;
                return (
                  <motion.button
                    key={`lower-${rodIdx}-${beadIdx}`}
                    data-testid={`abacus-lower-bead-rod-${rodIdx}-index-${beadIdx}`}
                    data-active={isActive ? "true" : "false"}
                    aria-label={`Rod ${rodIdx + 1} lower bead ${beadIdx + 1} (value 1)`}
                    onClick={() => setLower(rodIdx, beadIdx)}
                    disabled={readOnly}
                    className={`absolute rounded-full focus:outline-none ${readOnly ? "cursor-default" : ""} ${isActive ? "" : "opacity-55"}`}
                    style={{
                      left: x - beadWidth / 2,
                      width: beadWidth,
                      height: beadHeight,
                      background: isActive ? lowerActiveBg : lowerInactiveBg,
                      border: isActive ? "1px solid rgba(0,0,0,0.25)" : "1px solid rgba(0,0,0,0.10)",
                      boxShadow: isActive ? activeRingShadow : inactiveShadow,
                      filter: isActive ? "none" : "saturate(0.55)",
                    }}
                    animate={{ top: y }}
                    transition={readOnly ? { duration: 0 } : { type: "spring", stiffness: 500, damping: 24 }}
                    whileHover={readOnly ? undefined : { scale: 1.05 }}
                    whileTap={readOnly ? undefined : { scale: 0.95 }}
                  />
                );
              });
            })}

            {/* Place-value labels */}
            <div className="absolute left-0 right-0" style={{ top: totalHeight - 24 }}>
              {Array.from({ length: rods }).map((_, i) => {
                const label = labelFor(i, resolvedOnes);
                const isOnes = i === resolvedOnes;
                return (
                  <div
                    key={`label-${i}`}
                    data-testid={`abacus-label-${i}`}
                    className={`absolute font-bold uppercase tracking-wider text-center ${rods > 8 ? "text-[10px]" : "text-xs sm:text-sm"} ${isOnes ? "text-white" : "text-white/70"}`}
                    style={{ left: i * rodWidth, width: rodWidth }}
                  >
                    {label}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Value + Reset — hidden entirely in readOnly (represent) mode so the answer isn't given away */}
      {!readOnly && (
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-baseline gap-3">
            <span className="text-xs font-bold uppercase tracking-wider text-stone">Abacus reads</span>
            <span data-testid="abacus-value" className="font-heading text-3xl font-bold text-ink tabular-nums">
              {formatValue(value)}
            </span>
          </div>
          <button
            data-testid="abacus-reset-button"
            onClick={reset}
            type="button"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border-2 border-cloud text-ink font-bold text-sm btn-tactile hover:border-sky hover:text-sky"
          >
            Reset Abacus
          </button>
        </div>
      )}
    </div>
  );
});

export default VirtualAbacus;
